import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Clock, CheckCircle, AlertTriangle, XCircle, ChevronDown, ChevronUp,
  Activity, Pill, Utensils, Leaf, Calendar, User, RefreshCw,
  ArrowRight, AlertCircle, Zap, TrendingDown
} from 'lucide-react';

const API = '/api';

const BLOCK_LABELS = {
  wake: 'Wake',
  morning: 'Morning',
  mid_morning: 'Mid-Morning',
  lunch: 'Lunch',
  afternoon: 'Afternoon',
  evening: 'Evening',
  dinner: 'Dinner',
  night: 'Night',
  sleep: 'Sleep'
};

const EVENT_TYPE_CONFIG = {
  lifeflo:    { icon: Leaf,     color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', label: 'LifeFlō' },
  regenflo:   { icon: Activity, color: 'text-blue-400',    bg: 'bg-blue-500/10',    border: 'border-blue-500/20',    label: 'ReGenflō' },
  medication: { icon: Pill,     color: 'text-amber-400',   bg: 'bg-amber-500/10',   border: 'border-amber-500/20',   label: 'Medication' },
  food:       { icon: Utensils, color: 'text-rose-400',    bg: 'bg-rose-500/10',    border: 'border-rose-500/20',    label: 'Food' },
  therapy:    { icon: Activity, color: 'text-purple-400',  bg: 'bg-purple-500/10',  border: 'border-purple-500/20',  label: 'Therapy' },
  assessment: { icon: Clock,    color: 'text-cyan-400',    bg: 'bg-cyan-500/10',    border: 'border-cyan-500/20',    label: 'Assessment' },
};

const STATUS_CONFIG = {
  as_scheduled: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', label: 'As Scheduled', icon: CheckCircle },
  changed:      { color: 'text-amber-400',   bg: 'bg-amber-500/10',   label: 'Changed',      icon: AlertTriangle },
  skipped:      { color: 'text-red-400',     bg: 'bg-red-500/10',     label: 'Skipped',      icon: XCircle },
  pending:      { color: 'text-white/30',    bg: 'bg-adm-surface/5',        label: 'Pending',      icon: Clock },
};

function SummaryStrip({ summary }) {
  return (
    <div className="bg-adm-surface/[0.02] border border-white/[0.06] rounded-lg p-4 mb-6">
      <div className="grid grid-cols-7 gap-4">
        <div className="text-center">
          <div className="text-2xl font-bold text-white/90">{summary.total_events}</div>
          <div className="text-[10px] text-white/40 mt-1">Total Events</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-emerald-400">{summary.as_scheduled}</div>
          <div className="text-[10px] text-white/40 mt-1">As Scheduled</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-amber-400">{summary.changed}</div>
          <div className="text-[10px] text-white/40 mt-1">Changed</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-red-400">{summary.skipped}</div>
          <div className="text-[10px] text-white/40 mt-1">Skipped</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-white/50">{summary.pending}</div>
          <div className="text-[10px] text-white/40 mt-1">Pending</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-400">{summary.exact_timing_pct}%</div>
          <div className="text-[10px] text-white/40 mt-1">Exact Timing</div>
        </div>
        <div className="text-center">
          <div className="text-sm font-bold text-rose-400">{summary.main_friction_zone || '—'}</div>
          <div className="text-[10px] text-white/40 mt-1">Friction Zone</div>
          {summary.main_friction_rate > 0 && (
            <div className="text-[9px] text-rose-400/60">{summary.main_friction_rate}% unstable</div>
          )}
        </div>
      </div>
    </div>
  );
}

function RuleOverlay({ violations }) {
  if (!violations || violations.length === 0) return null;
  
  const grouped = violations.reduce((acc, v) => {
    const key = v.rule;
    if (!acc[key]) acc[key] = [];
    acc[key].push(v);
    return acc;
  }, {});
  
  return (
    <div className="mb-6 space-y-2">
      {Object.entries(grouped).map(([rule, items]) => {
        const hasRed = items.some(i => i.severity === 'red');
        const hasYellow = items.some(i => i.severity === 'yellow');
        const bgColor = hasRed ? 'bg-red-500/10 border-red-500/20' : hasYellow ? 'bg-amber-500/10 border-amber-500/20' : 'bg-emerald-500/10 border-emerald-500/20';
        
        return (
          <div key={rule} className={`rounded-lg p-3 border ${bgColor}`}>
            <div className="text-xs font-medium text-white/70 mb-2">{items[0].rule_name}</div>
            <div className="space-y-1">
              {items.map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <div className={`w-2 h-2 rounded-full ${item.severity === 'red' ? 'bg-red-400' : item.severity === 'yellow' ? 'bg-amber-400' : 'bg-emerald-400'}`} />
                  <span className="text-white/50">{item.affected_codes?.join(' ↔ ')}</span>
                  <span className="text-white/70">{item.message}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DensityWarnings({ warnings }) {
  if (!warnings || warnings.length === 0) return null;
  
  return (
    <div className="mb-6">
      {warnings.map((w, i) => (
        <div key={i} className={`rounded-lg p-3 border mb-2 ${w.severity === 'red' ? 'bg-red-500/10 border-red-500/20' : 'bg-amber-500/10 border-amber-500/20'}`}>
          <div className="flex items-center gap-2 text-xs">
            <Zap className={`w-4 h-4 ${w.severity === 'red' ? 'text-red-400' : 'text-amber-400'}`} />
            <span className="text-white/70">{w.message}</span>
            <Badge className="text-[9px] bg-adm-surface/5 text-white/40 border-0">@{w.time_bucket}</Badge>
          </div>
        </div>
      ))}
    </div>
  );
}

function TimelineItem({ item, ruleViolations }) {
  const typeConfig = EVENT_TYPE_CONFIG[item.event_type] || EVENT_TYPE_CONFIG.regenflo;
  const statusConfig = STATUS_CONFIG[item.status] || STATUS_CONFIG.pending;
  const TypeIcon = typeConfig.icon;
  const StatusIcon = statusConfig.icon;
  
  // Check if this item has rule violations
  const itemViolations = ruleViolations?.filter(v => v.items?.includes(item.id)) || [];
  const hasRed = itemViolations.some(v => v.severity === 'red');
  const hasYellow = itemViolations.some(v => v.severity === 'yellow');
  
  const borderColor = hasRed ? 'border-red-500/40' : hasYellow ? 'border-amber-500/40' : 'border-white/[0.06]';
  
  return (
    <div className={`bg-adm-surface/[0.02] border ${borderColor} rounded-lg p-3 mb-2`} data-testid={`timeline-item-${item.id}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          {/* Time column */}
          <div className="w-20 text-right">
            <div className="text-xs font-mono text-white/40">{item.planned_time}</div>
            {item.actual_time && item.actual_time !== item.planned_time && (
              <div className="flex items-center justify-end gap-1 mt-0.5">
                <ArrowRight className="w-2 h-2 text-amber-400" />
                <span className="text-xs font-mono text-amber-400">{item.actual_time}</span>
              </div>
            )}
            {item.timing_variance_minutes != null && item.timing_variance_minutes !== 0 && (
              <div className={`text-[9px] ${Math.abs(item.timing_variance_minutes) > 15 ? 'text-amber-400' : 'text-white/30'}`}>
                {item.timing_variance_minutes > 0 ? '+' : ''}{item.timing_variance_minutes} min
              </div>
            )}
          </div>
          
          {/* Type badge */}
          <div className={`w-6 h-6 rounded ${typeConfig.bg} flex items-center justify-center flex-shrink-0`}>
            <TypeIcon className={`w-3.5 h-3.5 ${typeConfig.color}`} />
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono text-white/40">{item.code}</span>
              <span className="text-sm text-white/90 truncate">{item.name}</span>
            </div>
            
            {/* Planned vs Actual dose */}
            <div className="flex items-center gap-2 mt-1">
              {item.planned_dose && (
                <span className="text-[10px] text-white/40">
                  Planned: {item.planned_dose}
                </span>
              )}
              {item.actual_dose && item.actual_dose !== item.planned_dose && (
                <>
                  <ArrowRight className="w-2 h-2 text-amber-400" />
                  <span className="text-[10px] text-amber-400">
                    Actual: {item.actual_dose}
                  </span>
                </>
              )}
            </div>
            
            {/* Changed reason / note */}
            {item.changed_reason && (
              <div className="text-[10px] text-white/40 mt-1">
                Reason: <span className="text-amber-400/70">{item.changed_reason}</span>
              </div>
            )}
            {item.note && (
              <div className="text-[10px] text-white/40 italic mt-0.5">{item.note}</div>
            )}
            
            {/* Rule violations for this item */}
            {itemViolations.length > 0 && (
              <div className="mt-2 space-y-1">
                {itemViolations.map((v, i) => (
                  <div key={i} className={`text-[9px] flex items-center gap-1 ${v.severity === 'red' ? 'text-red-400' : v.severity === 'yellow' ? 'text-amber-400' : 'text-emerald-400'}`}>
                    <AlertCircle className="w-3 h-3" />
                    {v.rule_name}: {v.message}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Status badge */}
        <Badge className={`${statusConfig.bg} ${statusConfig.color} border-0 text-[9px] flex items-center gap-1`}>
          <StatusIcon className="w-3 h-3" />
          {statusConfig.label}
        </Badge>
      </div>
    </div>
  );
}

function GroupedItems({ group, ruleViolations }) {
  const [expanded, setExpanded] = useState(true);
  
  // Calculate group summary
  const statuses = group.items.map(i => i.status);
  const allDone = statuses.every(s => s === 'as_scheduled');
  const hasSkipped = statuses.some(s => s === 'skipped');
  const hasChanged = statuses.some(s => s === 'changed');
  
  const bgColor = hasSkipped ? 'bg-red-500/5' : hasChanged ? 'bg-amber-500/5' : allDone ? 'bg-emerald-500/5' : 'bg-adm-surface/[0.01]';
  
  return (
    <div className={`rounded-lg border border-white/[0.06] ${bgColor} mb-3 overflow-hidden`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-adm-surface/[0.02] transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono text-white/30">{group.planned_time}</span>
          <span className="text-xs font-medium text-white/70">{group.group_label}</span>
          <Badge className="text-[9px] bg-adm-surface/5 text-white/30 border-0">{group.items.length} items</Badge>
          {allDone && <CheckCircle className="w-3 h-3 text-emerald-400" />}
          {hasSkipped && <XCircle className="w-3 h-3 text-red-400" />}
          {hasChanged && !hasSkipped && <AlertTriangle className="w-3 h-3 text-amber-400" />}
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-white/20" /> : <ChevronDown className="w-4 h-4 text-white/20" />}
      </button>
      {expanded && (
        <div className="px-3 pb-3">
          {group.items.map(item => (
            <TimelineItem key={item.id} item={item} ruleViolations={ruleViolations} />
          ))}
        </div>
      )}
    </div>
  );
}

function TimelineBlock({ block, ruleViolations }) {
  const [expanded, setExpanded] = useState(true);
  
  const totalItems = block.summary.total;
  const completedItems = block.summary.as_scheduled + block.summary.changed;
  const isUnstable = block.instability_rate > 20;
  
  return (
    <div className="mb-6" data-testid={`timeline-block-${block.block}`}>
      {/* Block header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full flex items-center justify-between p-3 rounded-lg border transition-colors ${
          isUnstable ? 'bg-red-500/5 border-red-500/20' : 'bg-adm-surface/[0.02] border-white/[0.06]'
        } hover:bg-adm-surface/[0.04]`}
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-white/80">{BLOCK_LABELS[block.block] || block.block}</span>
          <Badge className="text-[9px] bg-adm-surface/5 text-white/40 border-0">{totalItems} items</Badge>
          <div className="flex items-center gap-1 text-[10px]">
            <span className="text-emerald-400">{block.summary.as_scheduled} ✓</span>
            <span className="text-amber-400">{block.summary.changed} Δ</span>
            <span className="text-red-400">{block.summary.skipped} ✗</span>
          </div>
          {isUnstable && (
            <Badge className="text-[9px] bg-red-500/10 text-red-400 border-0 flex items-center gap-1">
              <TrendingDown className="w-3 h-3" /> {block.instability_rate}% unstable
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Mini progress bar */}
          <div className="w-20 h-1.5 bg-adm-surface/5 rounded-full overflow-hidden">
            <div 
              className="h-full bg-emerald-500/50" 
              style={{ width: `${(completedItems / totalItems) * 100}%` }}
            />
          </div>
          {expanded ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
        </div>
      </button>
      
      {/* Block content */}
      {expanded && (
        <div className="mt-3 pl-4 border-l-2 border-white/[0.06]">
          {/* Grouped items */}
          {block.grouped_items?.map(group => (
            <GroupedItems key={group.group_key} group={group} ruleViolations={ruleViolations} />
          ))}
          
          {/* Ungrouped items */}
          {block.ungrouped_items?.map(item => (
            <TimelineItem key={item.id} item={item} ruleViolations={ruleViolations} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function TimelineReview() {
  const { user } = useAuth();
  const [subscribers, setSubscribers] = useState([]);
  const [selectedSubscriber, setSelectedSubscriber] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const token = localStorage.getItem('rf_access_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  
  // Fetch subscribers
  useEffect(() => {
    fetch(`${API}/admin/v3/subscribers-list`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.subscribers) {
          setSubscribers(d.subscribers);
          // Auto-select first subscriber with active phase
          const withPhase = d.subscribers.find(s => s.has_active_phase);
          if (withPhase) setSelectedSubscriber(withPhase.id);
        }
      });
  }, []);
  
  // Fetch timeline data
  const fetchTimeline = useCallback(async () => {
    if (!selectedSubscriber || !selectedDate) return;
    setLoading(true);
    
    const res = await fetch(
      `${API}/admin/v3/timeline-review/${selectedSubscriber}?date=${selectedDate}`, 
      { headers }
    );
    if (res.ok) {
      setData(await res.json());
    }
    setLoading(false);
  }, [selectedSubscriber, selectedDate]);
  
  useEffect(() => {
    fetchTimeline();
  }, [fetchTimeline]);
  
  return (
    <AdminLayout>
      <div className="p-6 max-w-[1200px] mx-auto" data-testid="timeline-review">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-semibold text-white/90">Timeline Review</h1>
            <p className="text-xs text-white/40 mt-1">Day review: What was planned? What actually happened?</p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Subscriber selector */}
            <Select value={selectedSubscriber} onValueChange={setSelectedSubscriber}>
              <SelectTrigger className="w-[220px] bg-adm-surface/5 border-white/10 text-white h-9 text-xs">
                <User className="w-3 h-3 mr-2 text-white/40" />
                <SelectValue placeholder="Select subscriber" />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0a0f] border-white/10">
                {subscribers.map(s => (
                  <SelectItem key={s.id} value={s.id} className="text-white text-xs">
                    <span className={s.has_active_phase ? 'text-white' : 'text-white/40'}>{s.full_name}</span>
                    {!s.has_active_phase && <span className="text-white/20 ml-2">(no phase)</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Date picker */}
            <div className="relative">
              <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-white/40 pointer-events-none" />
              <Input
                type="date"
                value={selectedDate}
                onChange={e => setSelectedDate(e.target.value)}
                className="w-[150px] bg-adm-surface/5 border-white/10 text-white h-9 text-xs pl-7"
              />
            </div>
            
            {/* Refresh */}
            <Button onClick={fetchTimeline} size="sm" variant="ghost" className="text-xs text-white/50 h-9">
              <RefreshCw className="w-3 h-3 mr-1" /> Refresh
            </Button>
          </div>
        </div>
        
        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center h-64 text-white/40">Loading timeline...</div>
        ) : !data ? (
          <div className="text-center py-12 text-white/40">
            <Clock className="w-12 h-12 mx-auto mb-4 opacity-20" />
            <p>Select a subscriber and date to view timeline</p>
          </div>
        ) : !data.has_data ? (
          <div className="text-center py-12 text-white/40">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-20" />
            <p>{data.message || 'No data available'}</p>
          </div>
        ) : (
          <>
            {/* Subscriber & Phase info */}
            <Card className="bg-adm-surface/[0.02] border-white/[0.06] mb-6">
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                    <User className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-white/90">{data.subscriber?.full_name}</div>
                    <div className="text-xs text-white/40">{data.subscriber?.email}</div>
                  </div>
                </div>
                <div className="text-right">
                  <Badge className="bg-blue-500/10 text-blue-400 border-0">{data.phase?.name}</Badge>
                  <div className="text-[10px] text-white/30 mt-1">
                    {new Date(data.date).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Summary Strip */}
            <SummaryStrip summary={data.summary} />
            
            {/* Rule Overlays */}
            <RuleOverlay violations={data.rule_violations} />
            
            {/* Density Warnings */}
            <DensityWarnings warnings={data.density_warnings} />
            
            {/* Timeline Blocks */}
            <div className="space-y-4">
              {data.blocks?.map(block => (
                <TimelineBlock key={block.block} block={block} ruleViolations={data.rule_violations} />
              ))}
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
